// The in-memory twin of `POST recall/reach-preview` (D8 create/move dialog;
// services/api/src/recall/reach-preview.ts). The server answers with the Recall sync's own
// resolver; this twin follows the same rule over the structure and work twins' stores: every DE
// reads company knowledge and the company-level spaces (no team, public), its own seats
// (read and write), the teams its seats are members or leads of, and inside those teams'
// departments (a top-level team with its sub-teams) the public spaces and its own teams' private
// ones. Managers only, behind `memory`; another company's ids are 404. Nothing is written.
import { hierarchyError } from "@companyos/domain-work/org";
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem, validation } from "./fake";
import type { FakeCompany } from "./fake-data";
import type { Space } from "./fake-work-data";
import type { Seat, SeatAssignment } from "./types";

type Change = OperationInput<"recall.reachPreview">["body"];
type Preview = OperationOutput<"recall.reachPreview">;
type Agent = Preview["agents"][number];
type Item = Agent["after"][number];
type Team = {
  id: string;
  name: string;
  parent_team_id: string | null;
  lead_seat_id: string | null;
};
type Member = { team_id: string; seat_id: string };

export const FAKE_REACH_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>(["recall.reachPreview"]);

const NEW = "new";
type Org = {
  /** DE membership id → its active seats. */
  des: Map<string, string[]>;
  memberOf: Map<string, Set<string>>;
  leadOf: Map<string, string | null>;
  parentOf: Map<string, string | null>;
};

export function createReachFake(deps: {
  seatsOf: (c: FakeCompany) => Seat[];
  assignmentsOf: (c: FakeCompany) => SeatAssignment[];
  teamsOf: (c: FakeCompany) => Team[];
  teamMembersOf: (c: FakeCompany) => Member[];
  spacesOf: (companyId: string) => Space[] | null;
  nameOf: (companyId: string, membershipId: string) => string;
  /** A membership of the caller's company: a DE, a person, or null (not this company's). */
  memberKind: (p: Principal, membershipId: string) => "digital_employee" | "person" | null;
}): Partial<Record<RouteId, Handler>> {
  function orgOf(c: FakeCompany): Org {
    const des = new Map<string, string[]>();
    for (const a of deps.assignmentsOf(c))
      if (a.membership_kind === "digital_employee" && a.active_to === null)
        des.set(a.membership_id, [...(des.get(a.membership_id) ?? []), a.seat_id]);
    const memberOf = new Map<string, Set<string>>();
    for (const m of deps.teamMembersOf(c))
      memberOf.set(m.seat_id, (memberOf.get(m.seat_id) ?? new Set()).add(m.team_id));
    const teams = deps.teamsOf(c);
    return {
      des,
      memberOf,
      leadOf: new Map(teams.map((t) => [t.id, t.lead_seat_id])),
      parentOf: new Map(teams.map((t) => [t.id, t.parent_team_id])),
    };
  }

  /** `team` is `ancestor` or under it. */
  const under = (org: Org, team: string, ancestor: string) => {
    let at: string | null | undefined = team;
    for (let i = 0; at && i <= org.parentOf.size; i++) {
      if (at === ancestor) return true;
      at = org.parentOf.get(at);
    }
    return false;
  };
  /** `addMember`'s containment: a sub-team's seat is a member of its parent. */
  const contained = (org: Org, seat: string, team: string) => {
    const parent = org.parentOf.get(team);
    return !parent || org.memberOf.get(seat)?.has(parent) === true;
  };

  /** Applies the change; a string: why the change itself would be refused (409). */
  function apply(org: Org, ch: Change, isDe: boolean): string | null {
    if (ch.kind === "seat_holder") {
      const id = ch.membership_id ?? NEW;
      if (ch.membership_id !== null && !isDe) return null; // a person's seat: no agent reach
      const add = ch.add_seat_id ?? (ch.membership_id === null ? `${NEW}:seat` : null);
      const seats = (org.des.get(id) ?? []).filter((s) => s !== ch.end_seat_id && s !== add);
      if (add && seats.length > 0) return "a digital employee holds one seat at a time";
      org.des.set(id, add ? [...seats, add] : seats);
      if (add && ch.add_team_id) {
        if (!contained(org, add, ch.add_team_id))
          return "the seat must be a member of the parent team first";
        org.memberOf.set(add, (org.memberOf.get(add) ?? new Set()).add(ch.add_team_id));
      }
    } else if (ch.kind === "seat_team") {
      const t = org.memberOf.get(ch.seat_id) ?? new Set<string>();
      // `removeMember` also takes the seat out of every team under the one it leaves
      if (ch.remove_team_id)
        for (const x of [...t]) if (under(org, x, ch.remove_team_id)) t.delete(x);
      org.memberOf.set(ch.seat_id, t);
      if (ch.add_team_id) {
        if (!contained(org, ch.seat_id, ch.add_team_id))
          return "the seat must be a member of the parent team first";
        t.add(ch.add_team_id);
      }
    } else {
      const id = ch.team_id ?? `${NEW}:team`;
      org.parentOf.set(id, ch.parent_team_id);
      org.leadOf.set(id, ch.lead_seat_id);
    }
    return null;
  }

  function reach(c: FakeCompany, org: Org, de: string): Map<string, Item> {
    const seats = org.des.get(de) ?? [];
    const out = new Map<string, Item>();
    if (seats.length === 0) return out;
    const teamName = (id: string) =>
      deps.teamsOf(c).find((t) => t.id === id)?.name ?? "The new team";
    const deptOf = (teamId: string) => {
      let at = teamId;
      for (let i = 0; i <= org.parentOf.size; i++) {
        const up = org.parentOf.get(at);
        if (!up) break;
        at = up;
      }
      return at;
    };
    const dept = (teamId: string) => {
      const d = deptOf(teamId);
      return { id: d.startsWith(NEW) ? null : d, name: teamName(d) };
    };
    const teams = new Set<string>();
    for (const s of seats) for (const t of org.memberOf.get(s) ?? []) teams.add(t);
    for (const [t, lead] of org.leadOf) if (lead && seats.includes(lead)) teams.add(t);
    const departments = new Set([...teams].map(deptOf));
    out.set("company", {
      kind: "company",
      id: null,
      name: "Company knowledge",
      department: null,
      access: "read",
    });
    for (const sp of deps.spacesOf(c.id) ?? []) {
      if (sp.kind === "personal" || sp.archived_at) continue;
      const ok =
        sp.team_id === null
          ? !sp.is_private
          : departments.has(deptOf(sp.team_id)) && (!sp.is_private || teams.has(sp.team_id));
      if (ok)
        out.set(`space:${sp.id}`, {
          kind: "space",
          id: sp.id,
          name: sp.name,
          department: sp.team_id ? dept(sp.team_id) : null,
          access: "read",
        });
    }
    for (const t of teams)
      out.set(`team:${t}`, {
        kind: "team",
        id: t.startsWith(NEW) ? null : t,
        name: teamName(t),
        department: dept(t),
        access: "read",
      });
    for (const s of seats)
      out.set(`seat:${s}`, {
        kind: "seat",
        id: s.startsWith(NEW) ? null : s,
        name: deps.seatsOf(c).find((x) => x.id === s)?.title ?? "Its new seat",
        department: null,
        access: "read_write",
      });
    return out;
  }

  const ORDER = { company: 0, space: 1, team: 2, seat: 3 } as const;
  const sorted = (m: Map<string, Item>) =>
    [...m.entries()].sort(
      ([, a], [, b]) => ORDER[a.kind] - ORDER[b.kind] || a.name.localeCompare(b.name),
    );

  return {
    "recall.reachPreview": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      const ch = body as Change | null;
      if (!ch || !["seat_holder", "seat_team", "team"].includes(String(ch.kind)))
        return validation("kind: seat_holder, seat_team or team");
      const c = p.company;
      const seatIds = new Set(deps.seatsOf(c).map((s) => s.id));
      const teamIds = new Set(deps.teamsOf(c).map((t) => t.id));
      const seatsNamed =
        ch.kind === "seat_holder"
          ? [ch.add_seat_id, ch.end_seat_id]
          : ch.kind === "seat_team"
            ? [ch.seat_id]
            : [ch.lead_seat_id];
      const teamsNamed =
        ch.kind === "seat_team"
          ? [ch.add_team_id, ch.remove_team_id]
          : ch.kind === "team"
            ? [ch.team_id, ch.parent_team_id]
            : [ch.add_team_id ?? null];
      const kind =
        ch.kind === "seat_holder" && ch.membership_id !== null
          ? deps.memberKind(p, ch.membership_id)
          : null;
      if (
        seatsNamed.some((s) => s && !seatIds.has(s)) ||
        teamsNamed.some((t) => t && !teamIds.has(t)) ||
        (ch.kind === "seat_holder" && ch.membership_id !== null && kind === null)
      )
        return notFound();
      const before = orgOf(c);
      const after = orgOf(c);
      // a team moved under another parent: the PATCH's own hierarchy rules (cycle 422, 409)
      if (ch.kind === "team" && ch.team_id !== null) {
        const graph = deps.teamsOf(c).map((t) => ({
          id: t.id,
          parentTeamId: t.parent_team_id,
          memberIds: deps
            .teamMembersOf(c)
            .filter((m) => m.team_id === t.id)
            .map((m) => m.seat_id),
        }));
        const node = graph.find((n) => n.id === ch.team_id);
        const error =
          node && node.parentTeamId !== ch.parent_team_id
            ? hierarchyError({ ...node, parentTeamId: ch.parent_team_id }, graph)
            : null;
        if (error?.code === "cycle") return problem(422, "org_chart_cycle", "Cycle", error.message);
        if (error) return problem(409, "conflict", "Conflict", error.message);
      }
      const refused = apply(after, ch, kind === "digital_employee");
      if (refused) return problem(409, "conflict", "Conflict", refused);
      const target = ch.kind === "seat_holder" ? (ch.membership_id ?? NEW) : null;
      const agents: Agent[] = [];
      for (const id of new Set([...before.des.keys(), ...after.des.keys()])) {
        const a = sorted(reach(c, before, id));
        const b = sorted(reach(c, after, id));
        const same = a.length === b.length && a.every(([k], i) => k === b[i]?.[0]);
        if (same && id !== target) continue;
        const ka = new Set(a.map(([k]) => k));
        const kb = new Set(b.map(([k]) => k));
        agents.push({
          membership_id: id === NEW ? null : id,
          name: id === NEW ? "New digital employee" : deps.nameOf(c.id, id),
          kind: "digital_employee",
          before: a.map(([, i]) => i),
          after: b.map(([, i]) => i),
          gains: b.filter(([k]) => !ka.has(k)).map(([, i]) => i),
          loses: a.filter(([k]) => !kb.has(k)).map(([, i]) => i),
        });
      }
      const first = (x: Agent) => ((x.membership_id ?? NEW) === target ? 0 : 1);
      agents.sort((x, y) => first(x) - first(y) || x.name.localeCompare(y.name));
      return json({ agents } satisfies Preview);
    },
  };
}
